import cuid from "cuid";
import { filter, get, map } from "lodash";
import omitDeep from "omit-deep-lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLazyQuery, useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import {
  CREATE_ATTACHMENT,
  DELETE_ATTACHMENT,
} from "utils/api/graphql/mutations/attachment";
import { DOWNLOAD_SINGLE_DOCUMENT } from "utils/api/graphql/mutations/download-document";
import { SUBSCRIPTION } from "utils/api/graphql/queries/subscription";
import {
  ATTACHMENT_TYPES,
  DOCUMENTS_TYPES,
  KEYS,
  RELATIONSHIP,
} from "utils/constants";
import { downloadDocument } from "utils/helpers/files";
import {
  isLastJustificationStep,
  JUSTIFICATION_STEPS,
} from "screens/Private/Broker/Projects/Detail/Templates/Comparison/Acoris/utils/documentsCompletion";
import { resolveIdentityDocumentSides } from "screens/Private/Broker/Projects/Detail/Templates/Comparison/Acoris/utils/identityDocuments";
import useSaveDocumentsCompletion from "screens/Private/Broker/Projects/Detail/Templates/Comparison/Acoris/utils/useSaveDocumentsCompletion";
import Loader from "../Components/Loader";
import Navbar from "../Components/Navbar/Navbar";
import View from "./View";

export const DOCUMENTS_TYPE = {
  socialSecurityCertificate:
    ATTACHMENT_TYPES.HEALTH_INSURANCE_RIGHTS_CERTIFICATE,
  cinRecto: ATTACHMENT_TYPES.CIN_RECTO,
  cinVerso: ATTACHMENT_TYPES.CIN_VERSO,
};

const SpouseJustification = ({
  currentStatus,
  tracer,
  project,
  onBack,
  updateUrl,
  onNext,
}) => {
  const [initialValues, setInitialValues] = useState(null);
  const { t } = useTranslation(NAME_SPACES.PUBLIC.ACORIS);
  const TRANSLATION = t("SPOUSE_JUSTIFICATION", {
    returnObjects: true,
  });
  const subscriptionId =
    get(project, "fields.currentSubscription") ||
    localStorage.getItem(KEYS.SUBSCRIPTION_ID);

  const { data } = useQuery(SUBSCRIPTION, {
    variables: { where: { id: subscriptionId } },
  });

  const spouse = get(project, "contact.relatedPersons", []).find(
    ({ relationship }) => relationship === RELATIONSHIP.SPOUSE,
  );
  const spouseUserId = get(spouse, "identity.user.id");

  // Get adherent and children user IDs
  const adherentUserId =
    get(data, "subscription.project.contact.user.id") ||
    get(project, "contact.user.id");

  const children = get(project, "contact.relatedPersons", []).filter(
    ({ relationship }) => relationship === RELATIONSHIP.CHILD,
  );

  const childrenUserIds = children
    .map((child) => get(child, "identity.user.id"))
    .filter(Boolean);

  const { loading: attachmentLoading, refetch: refetchAttachments } = useQuery(
    graphql.queries.ATTACHMENTS,
    {
      fetchPolicy: "no-cache",
      variables: {
        where: {
          AND: [
            { subscription: { id: subscriptionId } },
            { user: { id: spouseUserId } },
          ],
        },
        isIn: {
          type: Object.values(DOCUMENTS_TYPE),
        },
      },
      skip: !spouseUserId,
      onCompleted: ({ attachments: { data: attachmentsList } }) => {
        const spouseIdentity = resolveIdentityDocumentSides(attachmentsList, {
          rectoType: ATTACHMENT_TYPES.CIN_RECTO,
          versoType: ATTACHMENT_TYPES.CIN_VERSO,
          legacyType: ATTACHMENT_TYPES.CIN,
        });

        setInitialValues({
          socialSecurityCertificate: map(
            filter(
              attachmentsList,
              ({ type }) =>
                type === ATTACHMENT_TYPES.HEALTH_INSURANCE_RIGHTS_CERTIFICATE,
            ),
            ({ id, name, fileUrl }) => ({
              uid: id,
              name,
              status: "done",
              url: fileUrl,
            }),
          ),
          cinRecto: spouseIdentity.recto,
          cinVerso: spouseIdentity.verso,
        });
      },
    },
  );

  const [addAttachment] = useMutation(CREATE_ATTACHMENT);
  const [deleteAttachment] = useMutation(DELETE_ATTACHMENT);
  const [downloadSingleDocument] = useMutation(DOWNLOAD_SINGLE_DOCUMENT);
  const [getAllAttachments] = useLazyQuery(graphql.queries.ATTACHMENTS);
  const saveDocumentsCompletion = useSaveDocumentsCompletion(subscriptionId);

  const onSubmit = async (documents) => {
    const documentsToUpload = [];
    if (!spouseUserId) {
      return;
    }

    Object.entries(documents)?.map(([key, attachments]) =>
      attachments
        ?.filter((e) => !e.status)
        .forEach(({ name, contentType, base64 }) => {
          documentsToUpload.push({
            id: cuid(),
            subscription: { id: subscriptionId },
            user: { id: spouseUserId },
            name,
            content: base64,
            contentType,
            type: DOCUMENTS_TYPE[key],
          });
        }),
    );

    await Promise.all(
      documentsToUpload.map((document) =>
        addAttachment({
          variables: {
            data: document,
          },
        }),
      ),
    );

    const hasChildOver20 = get(
      project,
      "fields.hasRelatedPersons.hasChildOver20",
    );

    if (!hasChildOver20) {
      const allUserIds = [
        adherentUserId,
        spouseUserId,
        ...childrenUserIds,
      ].filter(Boolean);

      if (allUserIds.length > 0) {
        await getAllAttachments({
          fetchPolicy: "no-cache",
          variables: {
            where: {
              AND: [
                {
                  subscription: { id: subscriptionId },
                },
                {
                  OR: allUserIds.map((id) => ({ user: { id } })),
                },
              ],
            },
            isIn: {
              type: [
                DOCUMENTS_TYPES.CIN,
                DOCUMENTS_TYPES.PAYMENT_ACCOUNT,
                DOCUMENTS_TYPES.PROFESSIONAL_PAYMENT_ACCOUNT,
                DOCUMENTS_TYPES.STUDENT_ID_OR_ENROLLMENT_CERTIFICATE,
                DOCUMENTS_TYPES.SOCIAL_SECURITY_CERTIFICATE,
                DOCUMENTS_TYPES.PAY_SLIP,
                DOCUMENTS_TYPES.ELECTRONIC_SIGNATURE_ELIGIBILITY,
                ATTACHMENT_TYPES.CHILD_DEPENDENT_PROOF,
                ATTACHMENT_TYPES.OTHER,
              ],
            },
          },
        });
      }
    }

    await saveDocumentsCompletion(
      isLastJustificationStep(
        JUSTIFICATION_STEPS.SPOUSE,
        get(project, "fields.hasRelatedPersons"),
      ),
    );

    const source = localStorage.getItem(KEYS.SOURCE);
    updateUrl({
      subscriptionId,
      url: get(
        data,
        "subscription.insurancePlan.config.generatedUrl",
        `${process.env.REACT_APP_BASE_URL}/#/public/process/init/${source}`,
      ),
    });

    return onNext({
      payload: {
        fields: {
          ...get(project, "fields"),
        },
      },
      actionsTemplate: { subscriptionId },
    });
  };

  const downloadFile = async (id) => {
    await downloadSingleDocument({
      variables: { where: { id } },
      onCompleted: (_) => {
        const { document } = omitDeep(_.downloadSingleDocument, "__typename");
        downloadDocument(document);
      },
    });
  };

  const handleFileRemove = (fieldName) => async (item) => {
    const fileId = item?.uid || item?.id;

    // If file is already uploaded (has an id and doesn't contain "AUTO"), delete it
    if (fileId && typeof fileId === "string" && !fileId.includes("AUTO")) {
      try {
        await deleteAttachment({
          variables: {
            where: { id: fileId },
          },
        });
        // Refetch attachments to update the UI
        if (refetchAttachments) {
          await refetchAttachments();
        }
      } catch (_error) {
        // Attachment deletion failures should not block field clear.
      }
    }

    // Return the field name so View.js can update the form values
    return fieldName;
  };
  if (attachmentLoading || !initialValues || !spouseUserId) return <Loader />;

  return (
    <>
      <Navbar
        project={project}
        currentStatus={currentStatus}
        tracer={tracer}
        showProfile={true}
        showCart={true}
      />
      <View
        project={project}
        onSubmit={onSubmit}
        initialValues={initialValues}
        downloadFile={downloadFile}
        onBack={onBack}
        translate={TRANSLATION}
        onFileRemove={handleFileRemove}
        socialRegime={get(spouse, "identity.socialRegime")}
      />
    </>
  );
};

export default SpouseJustification;
